import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import GnNotebook, { GnNotebookRef } from './GnNotebook';
import MediaRecorderControl from './MediaRecorderControl';
import CueChip from './CueChip';
import AnswerCheckPanel from './AnswerCheckPanel';
import VoiceControl from './VoiceControl';
import InkLayer from './InkLayer';
import NarrationControl, { NarrationControlRef } from '../aicanvas/NarrationControl';
import { getGn, getOrCreateTopicGn, markPublishedSeen, saveGn, StudentGnDTO, updateViewPref } from '../../services/gnStudentService';
import { useCueObserver } from '../../hooks/useCueObserver';
import { useCanvasActionStreaming } from '../../hooks/useCanvasActionStreaming';
import { useVoice } from '../../hooks/useVoice';
import { CanvasAction } from '../../services/aiCanvasActionService';
import type { CueType } from '../../services/gnCueService';

export interface StudentGnPanelRef {
  /** Appends a block from the AI streaming pipeline — persists via the normal autosave (FR-040). */
  appendBlock: (blockType: 'TEXT' | 'IMAGE' | 'CHART', attrs: Record<string, any>) => void;
}

interface StudentGnPanelProps {
  courseId: number;
  /** Topic-linked GN. Ignored when interTopicGnId is set. */
  nodeId: number;
  /** Open a student-created inter-topic GN by id instead of the topic GN (FR-032). */
  interTopicGnId?: number;
  /** Live session: bump on session seq change so the tutor region refetches (SC-012). */
  publishRefreshKey?: number;
  onPasteDetected?: (text: string, position: { top: number; left: number }) => void;
  /** Extra toolbar items from the host page (chat toggle, etc). */
  toolbarExtras?: React.ReactNode;
}

const CUE_TO_ACTION: Record<CueType, CanvasAction> = {
  SOLVE: 'SOLVE_PROBLEM',
  UNDERSTAND_PROBLEM: 'UNDERSTAND_PROBLEM',
  EXPLAIN_TOPIC: 'UNDERSTAND_TOPIC',
  CHECK_ANSWER: 'CHECK_ANSWER',
  IMAGE_QUESTION: 'IMAGE_QUESTION',
};

/**
 * The student's GN for a topic (spec 046 US1/US2/US4): loads/creates her private document
 * (with the read-only tutor region inside), saves through the single student write path,
 * and hosts ambient help cues, answer checking, and the recording control. Used by both the
 * self-study page and the live-session view — the same notebook either way, per FR-006.
 */
const StudentGnPanel = forwardRef<StudentGnPanelRef, StudentGnPanelProps>(
  ({ courseId, nodeId, interTopicGnId, publishRefreshKey = 0, onPasteDetected, toolbarExtras }, ref) => {
    const [gn, setGn] = useState<StudentGnDTO | null>(null);
    const notebookRef = useRef<GnNotebookRef>(null);
    const gnIdRef = useRef<number | null>(null);
    const narrationRef = useRef<NarrationControlRef>(null);
    const pendingImageRef = useRef<string | null>(null);
    const wasGeneratingRef = useRef(false);

    useEffect(() => {
      let cancelled = false;
      setGn(null);
      const load = interTopicGnId != null ? getGn(interTopicGnId) : getOrCreateTopicGn(courseId, nodeId);
      load
        .then(dto => {
          if (cancelled) return;
          gnIdRef.current = dto.id;
          setGn(dto);
        })
        .catch(() => {});
      return () => { cancelled = true; };
    }, [courseId, nodeId, interTopicGnId]);

    const loadFn = useCallback(async () => gn?.blocks ?? null, [gn?.id]); // eslint-disable-line react-hooks/exhaustive-deps
    const saveFn = useCallback(async (blocks: string) => {
      if (gnIdRef.current != null) await saveGn(gnIdRef.current, blocks);
    }, []);

    const handleMarkSeen = useCallback(() => {
      if (gnIdRef.current != null) markPublishedSeen(gnIdRef.current).catch(() => {});
    }, []);

    const handleViewPrefChange = useCallback((viewPref: 'CANVAS' | 'PAGES') => {
      if (gnIdRef.current != null) updateViewPref(gnIdRef.current, viewPref).catch(() => {});
    }, []);

    const appendToCanvas = useCallback((blockType: 'TEXT' | 'IMAGE' | 'CHART', attrs: Record<string, any>) => {
      notebookRef.current?.canvas()?.appendBlock(blockType, attrs);
    }, []);

    const { triggerAction, stop, generating, streamingText, lastVerdict, clearVerdict } =
      useCanvasActionStreaming('student', courseId, nodeId, appendToCanvas, narrationRef);

    const { offer, observeCandidate, dismiss, clear } = useCueObserver('student', courseId, nodeId);

    // The observer only stores the position/cues, not the raw text — keep the last observed
    // text alongside so accept handlers can use it without threading it through cue state.
    const lastObservedTextRef = useRef('');

    // Voice question → the same triggerAction pipeline as a typed/cue-driven request, so
    // spoken and typed interactions get identical treatment (spec 046 FR-015).
    const voice = useVoice((transcript) => {
      lastObservedTextRef.current = transcript;
      triggerAction('UNDERSTAND_PROBLEM', transcript);
    });

    const handleReadAloud = () => {
      const editor = notebookRef.current?.canvas()?.getEditor();
      const text = editor?.getText() ?? '';
      if (text.trim()) voice.speak(text);
    };

    // Once generation finishes, clear the offer so the chip closes; CHECK_ANSWER's verdict
    // (if any) is surfaced separately via AnswerCheckPanel below.
    useEffect(() => {
      if (wasGeneratingRef.current && !generating) clear();
      wasGeneratingRef.current = generating;
    }, [generating, clear]);

    useImperativeHandle(ref, () => ({
      appendBlock: appendToCanvas,
    }), [appendToCanvas]);

    const handleAcceptCue = (type: CueType) => {
      const contentText = lastObservedTextRef.current;
      if (type === 'IMAGE_QUESTION' && pendingImageRef.current) {
        const base64 = pendingImageRef.current.split(',')[1] || '';
        triggerAction('IMAGE_QUESTION', contentText, { imageBase64: base64 });
      } else if (type === 'CHECK_ANSWER') {
        triggerAction('CHECK_ANSWER', '', { questionText: contentText, attemptText: contentText });
      } else {
        triggerAction(CUE_TO_ACTION[type], contentText);
      }
    };

    const handlePasteDetected = (text: string, position: { top: number; left: number }) => {
      lastObservedTextRef.current = text;
      pendingImageRef.current = null;
      observeCandidate(text, position);
      onPasteDetected?.(text, position);
    };

    const handleImagePasted = (imageDataUrl: string, position: { top: number; left: number }) => {
      lastObservedTextRef.current = '';
      pendingImageRef.current = imageDataUrl;
      observeCandidate('', position, imageDataUrl);
    };

    if (!gn) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%" p={4}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    return (
      <GnNotebook
        key={gn.id}
        ref={notebookRef}
        role="student"
        courseId={courseId}
        nodeId={nodeId}
        loadFn={loadFn}
        saveFn={saveFn}
        onPasteDetected={handlePasteDetected}
        onImagePasted={handleImagePasted}
        lastSeenPublishedAt={gn.lastSeenPublishedAt}
        onMarkPublishedSeen={handleMarkSeen}
        publishRefreshKey={publishRefreshKey}
        initialViewPref={gn.viewPref}
        onViewPrefChange={handleViewPrefChange}
        toolbar={
          <>
            <MediaRecorderControl
              role="student"
              courseId={courseId}
              nodeId={nodeId}
              onClipReady={(assetId, mediaType, durationSeconds) =>
                notebookRef.current?.canvas()?.insertGnNode('mediaClip', { assetId, mediaType, durationSeconds })
              }
            />
            <NarrationControl ref={narrationRef} />
            <VoiceControl voice={voice} />
            <Tooltip title="Read this notebook aloud">
              <IconButton size="small" onClick={handleReadAloud} data-testid="gn-read-aloud">
                <RecordVoiceOverIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <InkLayer
              role="student"
              onCommitStrokes={(strokes, width, height, interpretedText, confidence) =>
                notebookRef.current?.canvas()?.insertGnNode('inkBlock', {
                  strokes: JSON.stringify(strokes), width, height, interpretedText, confidence,
                  interpretedAt: new Date().toISOString(),
                })
              }
            />
            {toolbarExtras}
          </>
        }
      >
        <CueChip
          offer={offer}
          generating={generating}
          streamingText={streamingText}
          onAccept={handleAcceptCue}
          onStop={stop}
          onDismiss={dismiss}
        />
        {lastVerdict && (
          <Box sx={{ px: 2, pb: 1 }}>
            <AnswerCheckPanel
              verdict={lastVerdict}
              onRevealSolution={() => triggerAction('REVEAL_SOLUTION', '', { questionText: lastObservedTextRef.current })}
              onDismiss={clearVerdict}
            />
          </Box>
        )}
      </GnNotebook>
    );
  }
);

StudentGnPanel.displayName = 'StudentGnPanel';

export default StudentGnPanel;
